import { ArrowUpRight, Video, Users } from "lucide-react";

const PRESTATIONS = [
    {
        icon: Video,
        eyebrow: "Prestation 01",
        title: "Création de contenu UGC",
        text: "Je crée des vidéos UGC pour les marques, restaurants, établissements et entreprises qui souhaitent alimenter leurs réseaux sociaux et leurs campagnes avec du contenu pensé pour les codes des plateformes.",
        formats:
            "Reels, vidéos verticales, face caméra, voice-over, démonstration produit, expérience, storytelling et contenu lifestyle.",
        cta: "Voir les packs",
        href: "#packs",
    },
    {
        icon: Users,
        eyebrow: "Prestation 02",
        title: "Influence & collaborations",
        text: "Pour aller plus loin que la simple création de contenu : diffusion du contenu auprès de ma communauté sur Instagram. Placements produits, tests en avant-première, recommandations authentiques et couverture d'événements.",
        formats:
            "Reels partagés en feed, stories dédiées, tags marque, mentions et couverture live selon la campagne.",
        cta: "En parler",
        href: "#contact",
    },
];

const PACKS = [
    {
        name: "Pack Découverte",
        price: "à partir de 290€",
        desc: "Pour tester un créateur UGC sans risque.",
        items: [
            "1 vidéo UGC (15-30 sec)",
            "Script inclus",
            "1 hook optimisé",
            "Sous-titres intégrés",
            "Livraison HD",
            "1 révision",
        ],
        ideal: "lancement produit, test créatif, première collaboration.",
        cta: "Réserver un appel",
        accent: false,
    },
    {
        name: "Pack Performance",
        price: "à partir de 690€",
        desc: "Des contenus conçus pour être testés en publicité.",
        items: [
            "3 vidéos UGC (15-45 sec)",
            "3 hooks différents par vidéo",
            "Storyboard inclus",
            "Sous-titres inclus",
            "Droits publicitaires Meta & TikTok 3 mois",
            "2 révisions",
        ],
        bonus: "Analyse rapide de votre page produit.",
        cta: "Réserver un appel",
        accent: true,
    },
    {
        name: "Pack Ambassadeur",
        price: "sur devis",
        desc: "Production continue pour alimenter vos réseaux.",
        items: [
            "5 à 10 vidéos / mois",
            "Stories UGC",
            "Photos lifestyle",
            "Calendrier de production",
            "Priorité de réservation",
            "Optimisations mensuelles",
        ],
        ideal: "e-commerce, beauté, food, applications, tourisme.",
        cta: "Travaillons ensemble",
        accent: false,
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white"
        >
            <div className="max-w-[1600px] mx-auto">
                {/* Header */}
                <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
                    <div className="md:col-span-7">
                        <p className="eyebrow mb-5">Services</p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02]">
                            Deux façons de travailler{" "}
                            <span className="italic">ensemble</span>.
                        </h2>
                    </div>
                    <p className="md:col-span-5 text-[#4A4A4A] text-base">
                        Création UGC pour vos propres canaux, ou activation via ma communauté :
                        chaque collaboration est adaptée à votre marque, votre cible et vos
                        objectifs.
                    </p>
                </div>

                {/* Prestations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-24">
                    {PRESTATIONS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <article
                                key={p.title}
                                data-testid={`prestation-card-${i}`}
                                className="group relative border border-[#E5E5E5] p-8 md:p-10 hover:border-fuchsia transition-colors flex flex-col"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <span className="text-[10px] uppercase tracking-[0.28em] text-gold">
                                        {p.eyebrow}
                                    </span>
                                    <Icon className="w-6 h-6 text-fuchsia" strokeWidth={1.4} />
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-6">
                                    {p.title}
                                </h3>
                                <p className="text-[#4A4A4A] text-sm md:text-base leading-relaxed mb-5">
                                    {p.text}
                                </p>
                                <p className="text-[#4A4A4A] text-sm md:text-base leading-relaxed mb-8">
                                    <span className="text-ink font-medium">Formats possibles : </span>
                                    {p.formats}
                                </p>
                                <a
                                    href={p.href}
                                    data-testid={`prestation-cta-${i}`}
                                    className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-ink hover:text-fuchsia transition-colors"
                                >
                                    {p.cta}
                                    <ArrowUpRight className="w-4 h-4" />
                                </a>
                            </article>
                        );
                    })}
                </div>

                {/* Packs sub-section */}
                <div id="packs" className="pt-4">
                    <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
                        <div className="md:col-span-7">
                            <p className="eyebrow mb-5 !text-gold">Packs UGC</p>
                            <h3
                                className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.05]"
                                data-testid="packs-heading"
                            >
                                Trois <span className="italic">niveaux</span> pour votre création de contenu.
                            </h3>
                        </div>
                        <p className="md:col-span-5 text-[#4A4A4A] text-sm md:text-base">
                            Les packs sont une base — on les ajuste toujours à votre brief, votre
                            cible et vos objectifs ads.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PACKS.map((s, i) => (
                            <div
                                key={s.name}
                                data-testid={`service-card-${i}`}
                                className={`group relative p-8 md:p-10 border transition-colors duration-300 flex flex-col ${
                                    s.accent
                                        ? "bg-bordeaux text-white border-bordeaux"
                                        : "bg-white border-[#E5E5E5] hover:border-gold"
                                }`}
                            >
                                {s.accent && (
                                    <span className="absolute -top-3 left-8 bg-gold text-ink text-[10px] uppercase tracking-[0.22em] px-3 py-1">
                                        Le plus choisi
                                    </span>
                                )}
                                <h4 className="font-display text-3xl mb-3">{s.name}</h4>
                                <p
                                    className={`text-xs uppercase tracking-[0.22em] mb-6 ${
                                        s.accent ? "text-gold" : "text-fuchsia"
                                    }`}
                                >
                                    {s.price}
                                </p>
                                <p
                                    className={`text-sm leading-relaxed mb-7 ${
                                        s.accent ? "text-white/75" : "text-[#4A4A4A]"
                                    }`}
                                >
                                    {s.desc}
                                </p>
                                <ul className="space-y-3 mb-7">
                                    {s.items.map((it) => (
                                        <li
                                            key={it}
                                            className={`text-sm flex items-start gap-3 ${
                                                s.accent ? "text-white" : "text-ink"
                                            }`}
                                        >
                                            <span
                                                className={`mt-2 w-1.5 h-1.5 shrink-0 ${
                                                    s.accent ? "bg-gold" : "bg-fuchsia"
                                                }`}
                                            />
                                            {it}
                                        </li>
                                    ))}
                                </ul>

                                {s.bonus && (
                                    <div
                                        className={`mb-7 pl-4 border-l-2 ${
                                            s.accent ? "border-gold" : "border-fuchsia"
                                        }`}
                                    >
                                        <p
                                            className={`text-[10px] uppercase tracking-[0.22em] mb-1 ${
                                                s.accent ? "text-gold" : "text-fuchsia"
                                            }`}
                                        >
                                            Bonus
                                        </p>
                                        <p
                                            className={`text-sm ${
                                                s.accent ? "text-white/90" : "text-ink"
                                            }`}
                                        >
                                            {s.bonus}
                                        </p>
                                    </div>
                                )}

                                {s.ideal && (
                                    <div className="mb-8">
                                        <p
                                            className={`text-[10px] uppercase tracking-[0.22em] mb-1 ${
                                                s.accent ? "text-gold" : "text-[#999]"
                                            }`}
                                        >
                                            Idéal pour
                                        </p>
                                        <p
                                            className={`text-sm italic ${
                                                s.accent ? "text-white/85" : "text-[#4A4A4A]"
                                            }`}
                                        >
                                            {s.ideal}
                                        </p>
                                    </div>
                                )}

                                <a
                                    href="#contact"
                                    data-testid={`service-cta-${i}`}
                                    className={`mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] ${
                                        s.accent
                                            ? "text-white hover:text-gold"
                                            : "text-ink hover:text-fuchsia"
                                    } transition-colors`}
                                >
                                    {s.cta}
                                    <ArrowUpRight className="w-4 h-4" />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
